import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';

import { BookingFilters, BookingModalType, defaultBookingFilters } from '../../../../core/state/bookings/types';
import { BookingStatus } from '../../../../core/enums/BookingStatus';
import { BookingLayout } from '../../types';
import { Color } from '../../../../core/enums/Color';
import DeliveryZone from '../../../../core/types/model/DeliveryZone';
import { RootState } from '../../../../core/state/state';
import { Actions } from '../../../../core/state/bookings/actions';
import { getFilters } from '../../../../core/state/bookings/selectors';
import { useBookingLayout } from '../../hooks';
import { formatDate, getMondayOfWeek } from '../../../../core/utils/date';
import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import CheckBox from '../../../../components/form/checkbox/CheckBox';
import Input from '../../../../components/form/input/Input';
import { DATE_FORMAT_ISO } from '../../../../core/constants/date';
import DeliveryZoneBadge from '../../../components/delivery-zone-badge/DeliveryZoneBadge';
import { selectProjectSettings } from '../../../../core/state/bookingProjectSettings/selectors';
import { Size } from '../../../../core/enums/Size';

import './BookingFilterModal.scss';

interface FilterFormData {
  dateStart: string;
  dateEnd: string;
  selectedDate: string;
  deliveryZone1: boolean;
  deliveryZone2: boolean;
  deliveryZone3: boolean;
  deliveryZone4: boolean;
  deliveryZone5: boolean;
  deliveryZone6: boolean;
  deliveryZone7: boolean;
  deliveryZone8: boolean;
  deliveryZone9: boolean;
  deliveryZone10: boolean;
  deliveryZone11: boolean;
  deliveryZone12: boolean;
  deliveryZone13: boolean;
  deliveryZone14: boolean;
  deliveryZone15: boolean;
  statusOpen: boolean;
  statusPending: boolean;
  statusDone: boolean;
  statusRejected: boolean;
  statusCanceled: boolean;
  statusBlocked: boolean;
}

const convertToFormData = (filters: BookingFilters, selectedCalendarDate: Date): FilterFormData => ({
  dateStart: formatDate(filters.dateRange.start, DATE_FORMAT_ISO),
  dateEnd: formatDate(filters.dateRange.end, DATE_FORMAT_ISO),
  selectedDate: formatDate(selectedCalendarDate, DATE_FORMAT_ISO),
  deliveryZone1: filters.deliveryZones.includes(1),
  deliveryZone2: filters.deliveryZones.includes(2),
  deliveryZone3: filters.deliveryZones.includes(3),
  deliveryZone4: filters.deliveryZones.includes(4),
  deliveryZone5: filters.deliveryZones.includes(5),
  deliveryZone6: filters.deliveryZones.includes(6),
  deliveryZone7: filters.deliveryZones.includes(7),
  deliveryZone8: filters.deliveryZones.includes(8),
  deliveryZone9: filters.deliveryZones.includes(9),
  deliveryZone10: filters.deliveryZones.includes(10),
  deliveryZone11: filters.deliveryZones.includes(11),
  deliveryZone12: filters.deliveryZones.includes(12),
  deliveryZone13: filters.deliveryZones.includes(13),
  deliveryZone14: filters.deliveryZones.includes(14),
  deliveryZone15: filters.deliveryZones.includes(15),
  statusOpen: filters.statuses.includes(BookingStatus.Open),
  statusPending: filters.statuses.includes(BookingStatus.Pending),
  statusDone: filters.statuses.includes(BookingStatus.Done),
  statusRejected: filters.statuses.includes(BookingStatus.Rejected),
  statusCanceled: filters.statuses.includes(BookingStatus.Canceled),
  statusBlocked: filters.statuses.includes(BookingStatus.Blocked)
});

const BookingFiltersModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const layout = useBookingLayout();
  const modalState = useSelector((state: RootState) => state.bookings.modal);
  const isOpen = modalState.isOpen && modalState.data?.type === BookingModalType.Filters;

  const projectSettings = useSelector(selectProjectSettings);
  const projectDeliveryZones = projectSettings?.projectDeliveryZones ?? [];

  const filters = useSelector(getFilters);
  const selectedCalendarDate = useSelector((state: RootState) => state.bookings.selectedDate);

  const { handleSubmit, control, reset } = useForm<FilterFormData>({
    defaultValues: convertToFormData(filters, selectedCalendarDate)
  });

  useEffect(() => {
    reset(convertToFormData(filters, selectedCalendarDate));
  }, [filters, selectedCalendarDate, reset]);

  const resetFilters = useCallback(() => {
    reset(convertToFormData(defaultBookingFilters, selectedCalendarDate));
  }, [reset, selectedCalendarDate]);

  const closeModal = useCallback(() => {
    if (isOpen) {
      dispatch(Actions.closeModal());
    }
  }, [dispatch, isOpen]);

  const onSubmit = useCallback(
    (formData: FilterFormData) => {
      const newFilters = {
        ...filters,
        dateRange: {
          start: new Date(formData.dateStart),
          end: new Date(formData.dateEnd)
        },
        deliveryZones: [],
        statuses: []
      } as BookingFilters;

      if (formData.deliveryZone1) newFilters.deliveryZones.push(1);
      if (formData.deliveryZone2) newFilters.deliveryZones.push(2);
      if (formData.deliveryZone3) newFilters.deliveryZones.push(3);
      if (formData.deliveryZone4) newFilters.deliveryZones.push(4);
      if (formData.deliveryZone5) newFilters.deliveryZones.push(5);
      if (formData.deliveryZone6) newFilters.deliveryZones.push(6);
      if (formData.deliveryZone7) newFilters.deliveryZones.push(7);
      if (formData.deliveryZone8) newFilters.deliveryZones.push(8);
      if (formData.deliveryZone9) newFilters.deliveryZones.push(9);
      if (formData.deliveryZone10) newFilters.deliveryZones.push(10);
      if (formData.deliveryZone11) newFilters.deliveryZones.push(11);
      if (formData.deliveryZone12) newFilters.deliveryZones.push(12);
      if (formData.deliveryZone13) newFilters.deliveryZones.push(13);
      if (formData.deliveryZone14) newFilters.deliveryZones.push(14);
      if (formData.deliveryZone15) newFilters.deliveryZones.push(15);

      if (formData.statusDone) newFilters.statuses.push(BookingStatus.Done);
      if (formData.statusRejected) newFilters.statuses.push(BookingStatus.Rejected);
      if (formData.statusOpen) newFilters.statuses.push(BookingStatus.Open);
      if (formData.statusPending) newFilters.statuses.push(BookingStatus.Pending);
      if (formData.statusCanceled) newFilters.statuses.push(BookingStatus.Canceled);
      if (formData.statusBlocked) newFilters.statuses.push(BookingStatus.Blocked);

      dispatch(Actions.changeFilters(newFilters));
      dispatch(Actions.changeDate(getMondayOfWeek(new Date(formData.selectedDate))));
      closeModal();
    },
    [closeModal, dispatch, filters]
  );

  const title = <h3 className="modal-title">{t('booking.filters')}</h3>;
  const buttons = (
    <div className="modal-buttons">
      <div className="modal-buttons__left">
        <Button buttonTypes={['red-outline']} onClick={resetFilters}>
          {t('booking.filters.reset')}
        </Button>
      </div>
      <Button buttonTypes={['transparent']} color={Color.Blue} onClick={closeModal}>
        {t('common.cancel')}
      </Button>
      <Button buttonTypes={['blue']} type="submit" form="booking-filters">
        {t('booking.filters.apply')}
      </Button>
    </div>
  );

  if (!isOpen) {
    return <></>;
  }

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} title={title} toolbar={buttons}>
      <form className="booking-filter-modal" id="booking-filters" onSubmit={handleSubmit(onSubmit)}>
        <p className="form-label">{t('common.date')}</p>

        {layout === BookingLayout.List && (
          <div className="form-row">
            <Controller
              name="dateStart"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Input label={t('common.from')} id="date-start" name={name} type="date" value={value} onChange={(e) => onChange(e.target.value)} />
              )}
            />

            <Controller
              name="dateEnd"
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <Input label={t('common.to')} id="date-end" name={name} type="date" value={value} onChange={(e) => onChange(e.target.value)} />
              )}
            />
          </div>
        )}

        {layout === BookingLayout.Calendar && (
          <Controller
            name="selectedDate"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Input
                className="booking-filter-modal__date"
                id="calendar-selected-date"
                name={name}
                type="date"
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
          />
        )}

        <p className="form-label mt-3">{t('projectSettings.deliveryZones.title')}</p>

        <div className="booking-filter-modal__checkboxes">
          {projectDeliveryZones.map((deliveryZone: DeliveryZone) => (
            <Controller
              key={deliveryZone.creationIndex}
              name={`deliveryZone${deliveryZone.creationIndex as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15}`}
              control={control}
              render={({ field: { onChange, value, name } }) => (
                <CheckBox
                  id={`delivery-zone-${deliveryZone.creationIndex}`}
                  name={name}
                  checked={value}
                  onChange={onChange}
                  label={<DeliveryZoneBadge labelSize={Size.Small} id={deliveryZone.id} />}
                />
              )}
            />
          ))}
        </div>

        <p className="form-label mt-3">{t('common.status')}</p>

        <div className="booking-filter-modal__checkboxes">
          <Controller
            name="statusOpen"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <CheckBox id="status-open" name={name} label={t('booking.status.open')} checked={value} onChange={onChange} />
            )}
          />

          <Controller
            name="statusPending"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <CheckBox id="status-pending" name={name} label={t('booking.status.pending')} checked={value} onChange={onChange} />
            )}
          />

          <Controller
            name="statusDone"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <CheckBox id="status-done" name={name} label={t('booking.status.done')} checked={value} onChange={onChange} />
            )}
          />

          {layout === BookingLayout.List && (
            <>
              <Controller
                name="statusRejected"
                control={control}
                render={({ field: { onChange, value, name } }) => (
                  <CheckBox id="status-rejected" name={name} label={t('booking.status.rejected')} checked={value} onChange={onChange} />
                )}
              />
              <Controller
                name="statusCanceled"
                control={control}
                render={({ field: { onChange, value, name } }) => (
                  <CheckBox id="status-canceled" name={name} label={t('booking.status.canceled')} checked={value} onChange={onChange} />
                )}
              />
            </>
          )}

          <Controller
            name="statusBlocked"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <CheckBox id="status-blocked" name={name} label={t('booking.status.blocked')} checked={value} onChange={onChange} />
            )}
          />
        </div>
      </form>
    </Modal>
  );
};

export default BookingFiltersModal;
